/* Public product cache only; never persist cart/customer data or reserve stock. */
(function (root) {
  'use strict';
  const CATALOG_TTL = 5 * 60 * 1000, STOCK_TTL = 30 * 1000;
  const signature = cart => JSON.stringify([cart.currency, (cart.items || []).map(l =>
    [l.key || l.variant_id || l.id, l.product_id, l.handle, l.variant_title, l.quantity,
      (l.item_components || []).map(c => [c.variant_id || c.id, c.product_id, c.handle, c.variant_title, c.quantity])])]);
  function create({engine, json, rootPath = '/', storage, scope = '', now = Date.now, preload = () => {}}) {
    const select = engine.select || engine.diverse;
    const cache = new Map(), pending = new Map(), prepared = new Map();
    let epoch = 0, active = 0; const waiters = [];
    const storageKey = `kickis-upsell-public-v1:${scope}`;
    try {
      const stored = JSON.parse(storage?.getItem(storageKey) || '[]');
      if (Array.isArray(stored)) for (const [key, entry] of stored.slice(0, 41)) {
        const ttl = key === 'catalog' ? CATALOG_TTL : STOCK_TTL;
        if (typeof key === 'string' && Number.isFinite(entry?.at) && entry.at <= now() && now() - entry.at < ttl) cache.set(key, entry);
      }
    } catch (_) { /* Private mode, corrupt cache or disabled storage: fetch normally. */ }
    const persist = () => {
      try {
        const entries = [...cache].filter(([k, v]) => now() - v.at < (k === 'catalog' ? CATALOG_TTL : STOCK_TTL));
        const value = JSON.stringify([...entries.filter(([k]) => k === 'catalog'), ...entries.filter(([k]) => k !== 'catalog').slice(-40)]);
        if (value.length < 700000) storage?.setItem(storageKey, value);
      } catch (_) { /* Storage is optional. */ }
    };
    async function limited(fn) {
      if (active >= 4) await new Promise(resolve => waiters.push(resolve));
      else active++;
      try { return await fn(); }
      finally { const next = waiters.shift(); if (next) next(); else active--; }
    }
    function cached(key, ttl, load) {
      const entry = cache.get(key);
      if (entry && now() - entry.at < ttl) return Promise.resolve(entry.value);
      if (pending.has(key)) return pending.get(key);
      const promise = Promise.resolve().then(load).then(value => {
        cache.set(key, {value, at: now()});
        return value;
      }).finally(() => pending.delete(key));
      pending.set(key, promise); return promise;
    }
    const catalogPage = page => limited(() => json(`${rootPath}collections/all?view=kickis-upsell-catalog&sort_by=best-selling&page=${page}`));
    function catalog() {
      return cached('catalog', CATALOG_TTL, async () => {
        const first = await catalogPage(1);
        if (!Array.isArray(first.products) || !Number.isInteger(first.pages) || first.pages < 1 || first.pages > 20) throw Error('Invalid catalog');
        const rest = await Promise.all(Array.from({length: first.pages - 1}, (_, i) => catalogPage(i + 2)));
        if (rest.some(p => !Array.isArray(p.products))) throw Error('Invalid catalog page');
        return [first, ...rest].flatMap(p => p.products);
      });
    }
    function detail(p) {
      return cached(`product:${p.handle}`, STOCK_TTL, () => limited(async () => {
        const data = await json(`${rootPath}products/${encodeURIComponent(p.handle)}?view=kickis-upsell-data`);
        if (!Array.isArray(data.variants) || data.handle !== p.handle) throw Error('Invalid product detail');
        return data;
      }));
    }
    function invalidate() { epoch++; prepared.clear(); }
    function clearStock() {
      invalidate(); for (const key of cache.keys()) if (key !== 'catalog') cache.delete(key);
      try { storage?.removeItem(storageKey); } catch (_) { /* Optional. */ }
    }
    function prepare(cart, {seed = 0, previous = [], onProgress} = {}) {
      const key = signature(cart) + ':' + seed;
      const old = prepared.get(key);
      if (old && now() - old.at < STOCK_TTL) {
        if (onProgress) { old.listeners.add(onProgress); if (old.products.length || old.done) onProgress(old.products, old.done, {hasMore: old.hasMore}); }
        return old.promise;
      }
      const version = epoch, state = {at: now(), products: [], done: false, hasMore: false, listeners: new Set(onProgress ? [onProgress] : [])};
      // Prepared cart keys stay in memory only and are bounded.
      if (prepared.size >= 3) prepared.delete(prepared.keys().next().value);
      prepared.set(key, state);
      const notify = done => {
        state.done = done;
        for (const p of state.products) state.at = Math.min(state.at, cache.get(`product:${p.handle}`)?.at ?? state.at);
        // Only eight small card images, not hundreds of variant images.
        select(state.products, 8).forEach(p => preload(p.image));
        state.listeners.forEach(fn => fn(state.products.slice(), done, {hasMore: state.hasMore}));
      };
      state.promise = (async () => {
        if (!engine.cartLines(cart).length) { notify(true); return []; }
        const list = await catalog();
        const pools = [engine.score(list, cart, {seed, previous}).slice(0, 32),
          engine.inspiration ? engine.inspiration(list, cart, {seed, previous}).slice(0, 20) : []];
        const found = [];
        for (const [poolIndex, ranked] of pools.entries()) {
          if (poolIndex > 0 && select(state.products, 8).length >= 8) break;
          for (let i = 0; i < ranked.length && version === epoch; i += 4) {
            const result = await Promise.all(ranked.slice(i, i + 4).map(async p => {
              try { return engine.eligible(p, await detail(p)); } catch (_) { return null; }
            }));
            if (version !== epoch) return [];
            found.push(...result.filter(Boolean));
            state.products = found.sort((a, b) => b.score - a.score);
            state.hasMore = i + 4 < ranked.length || (poolIndex === 0 && pools[1].length > 0);
            // Finish matching accessories before filling spare places with inspiration.
            notify(false);
            const complete = poolIndex === 0 ? engine.diverse(state.products, 8, {fill: false}) : select(state.products, 8);
            if (complete.length >= 8) break;
          }
        }
        if (version !== epoch) return [];
        notify(true); persist(); return state.products;
      })().catch(error => { prepared.delete(key); throw error; });
      return state.promise;
    }
    return {prepare, invalidate, clearStock};
  }
  const api = {create, signature, CATALOG_TTL, STOCK_TTL};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.KickisUpsellLoader = api;
})(typeof window !== 'undefined' ? window : globalThis);
